import { useEffect, useMemo, useState } from 'react'
import { agendaLabService } from '../../services/api'

const unwrapPayload = (response) => response?.data?.data ?? response?.data ?? null

const emptyWorkload = {
  date: '',
  totalMinutesAvailable: 0,
  totalMinutesOccupied: 0,
  utilizationPercent: 0,
  appointments: []
}

const normalizeAppointment = (item) => ({
  appointmentId: item?.appointmentId || item?.AppointmentId || '',
  serviceName: item?.serviceName || item?.ServiceName || 'Servicio',
  clientName: item?.clientName || item?.ClientName || 'Cliente',
  startAtUtc: item?.startAtUtc || item?.StartAtUtc || '',
  endAtUtc: item?.endAtUtc || item?.EndAtUtc || '',
  startLocal: item?.startLocal || item?.StartLocal || item?.startAtUtc || item?.StartAtUtc || '',
  endLocal: item?.endLocal || item?.EndLocal || item?.endAtUtc || item?.EndAtUtc || '',
  status: String(item?.status || item?.Status || 'scheduled').toLowerCase()
})

const normalizeWorkload = (payload) => ({
  date: payload?.date || payload?.Date || '',
  totalMinutesAvailable: Number(payload?.totalMinutesAvailable ?? payload?.TotalMinutesAvailable ?? 0),
  totalMinutesOccupied: Number(payload?.totalMinutesOccupied ?? payload?.TotalMinutesOccupied ?? 0),
  utilizationPercent: Number(payload?.utilizationPercent ?? payload?.UtilizationPercent ?? 0),
  appointments: Array.isArray(payload?.appointments || payload?.Appointments)
    ? (payload?.appointments || payload?.Appointments).map(normalizeAppointment)
    : []
})

const formatHours = (minutes) => Number((Number(minutes || 0) / 60).toFixed(1))

const formatDateTime = (value) => {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '-'
  return parsed.toLocaleTimeString('es-CO', {
    hour: '2-digit',
    minute: '2-digit'
  })
}

const statusLabel = (status) => {
  switch (status) {
    case 'completed': return 'Cumplida'
    case 'rescheduled': return 'Reprogramada'
    case 'cancelled': return 'Cancelada'
    default: return 'Agendada'
  }
}

export default function AgendaLabWorkload() {
  const [data, setData] = useState(emptyWorkload)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true)
        setError('')
        const today = new Date().toISOString().slice(0, 10)
        const response = await agendaLabService.getWorkload(today)
        setData(normalizeWorkload(unwrapPayload(response)))
      } catch (err) {
        setError(err?.message || 'No se pudo cargar la carga laboral.')
        setData(emptyWorkload)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [])

  const summary = useMemo(() => {
    const availableMinutes = data.totalMinutesAvailable || 0
    const occupiedMinutes = data.totalMinutesOccupied || 0
    return {
      availableHours: formatHours(availableMinutes),
      occupiedHours: formatHours(occupiedMinutes),
      freeHours: formatHours(Math.max(0, availableMinutes - occupiedMinutes))
    }
  }, [data.totalMinutesAvailable, data.totalMinutesOccupied])

  if (loading) {
    return <div className="card p-4 text-sm text-gray-600">Cargando carga laboral...</div>
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Carga Laboral</h1>
        <p className="mt-1 text-sm text-gray-500">Resumen operativo del dia y citas agendadas.</p>
      </div>

      {error && <div className="card p-3 text-sm text-red-700 bg-red-50 border border-red-200">{error}</div>}

      <div className="grid gap-4 md:grid-cols-4">
        <div className="card p-5">
          <p className="text-sm text-gray-500">Horas disponibles</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{summary.availableHours}</p>
        </div>
        <div className="card p-5">
          <p className="text-sm text-gray-500">Horas ocupadas</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{summary.occupiedHours}</p>
        </div>
        <div className="card p-5">
          <p className="text-sm text-gray-500">Horas libres</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{summary.freeHours}</p>
        </div>
        <div className="card p-5">
          <p className="text-sm text-gray-500">Utilizacion</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">{data.utilizationPercent}%</p>
        </div>
      </div>

      <div className="card p-5">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Citas del dia</h2>
            <p className="mt-1 text-sm text-gray-500">{data.appointments.length} cita(s) consideradas en la carga laboral.</p>
          </div>
        </div>

        {data.appointments.length === 0 ? (
          <div className="mt-4 rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
            No hay citas para mostrar en la fecha consultada.
          </div>
        ) : (
          <div className="mt-4 space-y-3">
            {data.appointments.map((appointment) => (
              <div key={appointment.appointmentId} className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-4">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                  <div>
                    <p className="font-medium text-gray-900">{appointment.serviceName}</p>
                    <p className="mt-1 text-sm text-gray-600">{appointment.clientName}</p>
                  </div>
                  <div className="text-sm text-gray-600">
                    {formatDateTime(appointment.startLocal)} - {formatDateTime(appointment.endLocal)}
                  </div>
                  <div className="text-sm font-medium text-gray-700">
                    {statusLabel(appointment.status)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
